import { useCallback, useEffect, useMemo, useRef } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import {
  patientsApi,
  useAllergiesQuery,
  useUpdateAllergyMutation,
} from '@features/patients/store/api';
import { Box, Skeleton, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks/core';
import { useModal } from '@store/hooks/useModal';
import { useSnackbar } from 'notistack';

import { AllergiesCard } from '../components/AllergiesCard';
import { ClinicalCardFrame } from '../components/ClinicalCardFrame';
import { mapAllergiesToVM } from '../vm/mapClinicalToVM';

/** Undo handle returned by an optimistic RTK Query cache patch. */
interface CachePatch {
  undo: () => void;
}

interface V1AllergiesCardProps {
  patientId: string;
  /**
   * Fire the cardOpened impression on mount (default). Hosts that embed this
   * card inside another tracked surface (the context-bar edit modal, which
   * fires editModalOpened) pass false so one open isn't counted twice.
   */
  trackImpression?: boolean;
  /** 'embedded' hands the chrome and scroll to a dialog host. */
  variant?: 'card' | 'embedded';
  /** Embedded only: the dialog's close handler, rendered in the header. */
  onClose?: () => void;
}

/**
 * Data glue for the redesigned Allergies card on the V1 patient page. Reads
 * and writes through the existing per-resource allergy CRUD (add/edit via the
 * shared AddAllergyModal); "Mark inactive" is the soft PATCH
 * `{ is_active: false }`. The NKA assertion stays null until the API exposes
 * the backend `allergen_type='nka'` sentinel — the card then falls back to the
 * "not yet asked" empty state.
 */
export const V1AllergiesCard: React.FC<V1AllergiesCardProps> = ({
  patientId,
  trackImpression = true,
  variant = 'card',
  onClose,
}) => {
  const { data, isLoading, isError } = useAllergiesQuery({ patientId });
  const [updateAllergy] = useUpdateAllergyMutation();
  const dispatch = useAppDispatch();
  const { open } = useModal();
  const { enqueueSnackbar } = useSnackbar();
  const { clinicalCards: tracking } = useEventTracking();

  const allergies = useMemo(() => mapAllergiesToVM(data?.allergies ?? []), [data]);
  const rowsById = useMemo(
    () => new Map((data?.allergies ?? []).map((row) => [row.allergy_id, row])),
    [data],
  );

  const openedTracked = useRef(false);
  useEffect(() => {
    if (!trackImpression || openedTracked.current || !data) return;
    openedTracked.current = true;
    tracking.cardOpened({
      patient_id: patientId,
      item_type: 'allergy',
      item_count: allergies.length,
    });
  }, [allergies.length, data, patientId, tracking, trackImpression]);

  const handleAdd = useCallback(() => {
    tracking.addClicked({ patient_id: patientId, item_type: 'allergy' });
    open(MODAL_TYPES.ADD_ALLERGY, { patientId });
  }, [open, patientId, tracking]);

  const handleEdit = useCallback(
    (allergyId: string) => {
      tracking.editClicked({
        patient_id: patientId,
        item_type: 'allergy',
        item_id: allergyId,
      });
      const row = rowsById.get(allergyId);
      open(MODAL_TYPES.ADD_ALLERGY, {
        patientId,
        allergyId,
        defaultValues: row
          ? {
              allergen: row.allergen,
              reaction: row.reaction ?? undefined,
              severity: row.severity ?? undefined,
              onset_date: row.onset_date ?? undefined,
              is_active: row.is_active,
            }
          : undefined,
      });
    },
    [open, patientId, rowsById, tracking],
  );

  const setActive = useCallback(
    (allergyId: string, toActive: boolean) => {
      tracking.statusToggled({
        patient_id: patientId,
        item_type: 'allergy',
        item_id: allergyId,
        to_active: toActive,
      });
      // Optimistic: the chip flips instantly; reverted on error.
      const patch = dispatch(
        patientsApi.util.updateQueryData(
          'getV2ProviderPatientsByPatientIdAllergies',
          { patientId },
          (draft) => {
            const target = draft.allergies.find((row) => row.allergy_id === allergyId);
            if (target) target.is_active = toActive;
          },
        ),
      ) as unknown as CachePatch;
      updateAllergy({
        patientId,
        allergyId,
        updateAllergyRequest: { is_active: toActive },
      })
        .unwrap()
        .catch(() => {
          patch.undo();
          enqueueSnackbar(
            toActive
              ? 'Could not reactivate the allergy. Try again.'
              : 'Could not inactivate the allergy. Try again.',
            { variant: 'error' },
          );
        });
    },
    [dispatch, enqueueSnackbar, patientId, tracking, updateAllergy],
  );

  const handleInactivate = useCallback(
    (allergyId: string) => setActive(allergyId, false),
    [setActive],
  );
  const handleReactivate = useCallback(
    (allergyId: string) => setActive(allergyId, true),
    [setActive],
  );

  const handleFilterChanged = useCallback(
    (filter: 'all' | 'active' | 'inactive') => {
      tracking.filterChanged({ patient_id: patientId, item_type: 'allergy', filter });
    },
    [patientId, tracking],
  );

  if (isLoading || !data) {
    return (
      <ClinicalCardFrame title="Allergies" variant={variant} onClose={onClose}>
        {isError ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 2, py: 4, textAlign: 'center' }}
          >
            Allergies could not be loaded. Refresh to try again.
          </Typography>
        ) : (
          <Box sx={{ px: 2, pb: 2 }}>
            {[0, 1, 2].map((row) => (
              <Skeleton key={row} variant="rounded" height={44} sx={{ my: 1, borderRadius: 1 }} />
            ))}
          </Box>
        )}
      </ClinicalCardFrame>
    );
  }

  return (
    <AllergiesCard
      variant={variant}
      onClose={onClose}
      allergies={allergies}
      nka={null}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onInactivate={handleInactivate}
      onReactivate={handleReactivate}
      onFilterChanged={handleFilterChanged}
    />
  );
};
